import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithInterceptors } from "../../app/store/baseQueryWithReauth";
import { reducerPathEnums } from "../../shared/types";
import type { ActivityType } from "./types/activityType";

export const activityApi = createApi({
	reducerPath: reducerPathEnums.Activity,
	tagTypes: ["Lesson", "User"],
	baseQuery: baseQueryWithInterceptors,
	endpoints: (builder) => ({
		getActivities: builder.query<ActivityType[], string>({
			query: (type?: string) => {
				return type ? `activities?type=${type}` : "activities";
			},
		}),
		getBlogs: builder.query<ActivityType[], null>({
			query: () => {
				return "blogs";
			},
		}),
		getActivityById: builder.query<ActivityType, string>({
			query: (id) => `activities/${id}`,
		}),
		addActivity: builder.mutation<ActivityType, Partial<ActivityType>>({
			query: (newActivity) => ({
				url: "activities",
				method: "POST",
				body: newActivity,
			}),
		}),
		updateActivity: builder.mutation<
			ActivityType,
			{ id: string; changes: Partial<ActivityType> }
		>({
			query: ({ id, changes }) => ({
				url: `activities/${id}`,
				method: "PUT",
				body: changes,
			}),
		}),
		completeActivity: builder.mutation<
			ActivityType,
			{
				changes: {
					activityId: string;
					userId: string;
				};
			}
		>({
			query: ({ changes }) => ({
				url: "activities/complete",
				method: "POST",
				body: changes,
			}),
			invalidatesTags: (result, error, { changes }) => {
				return [{ type: "User", id: changes.userId }];
			},
		}),
		uncompleteActivity: builder.mutation<
			ActivityType,
			{
				changes: {
					activityId: string;
					userId: string;
				};
			}
		>({
			query: ({ changes }) => ({
				url: "activities/uncomplete",
				method: "POST",
				body: changes,
			}),
			invalidatesTags: (result, error, { changes }) => [
				{ type: "Lesson", id: changes.activityId },
				{
					type: "User",
					id: changes.userId,
				},
			],
		}),
		awardPointsForActivity: builder.mutation<
			ActivityType,
			{ userId: string; points: number }
		>({
			query: ({ userId, points }) => ({
				url: "activities/awardPoints",
				method: "POST",
				body: { userId, points },
				invalidatesTags: (
					result: ActivityType,
					_error: unknown,
					userId: string,
				) => [{ type: "Points", id: userId }],
			}),
		}),
		deleteActivity: builder.mutation<{ success: boolean; id: string }, string>({
			query: (id) => ({
				url: `activities/${id}`,
				method: "DELETE",
			}),
		}),
	}),
});

export const {
	useGetActivitiesQuery,
	useGetActivityByIdQuery,
	useAddActivityMutation,
	useUpdateActivityMutation,
	useCompleteActivityMutation,
	useUncompleteActivityMutation,
	useDeleteActivityMutation,
	useLazyGetActivitiesQuery,
	useAwardPointsForActivityMutation,
	useGetBlogsQuery,
} = activityApi;
